import { z } from 'zod';
// ── Sub-schemas ───────────────────────────────────────────────────────────────
const urlOrEmpty = z
    .string()
    .max(500)
    .refine(v => v === '' || z.string().url().safeParse(v).success, {
    message: 'Debe ser una URL válida o string vacío',
});
// Hotfix Seguimientos: el campo gemelo que existía en el schema Drizzle (json,
// declaración de columna, no se toca) se retiró por completo de aquí — Zod
// strippea cualquier key no declarada, así que ya no hay forma de que un
// payload externo lo vuelva a poblar.
// Fase 3a (decisiones.md 2026-09-30, A12): lista cerrada y orden de
// despliegue de los Mercados de una plataforma. CO se incluye por
// MercadoLibre aunque Amazon no tenga marketplace ahí.
export const MERCADOS = ['MX', 'US', 'UK', 'CO', 'BR', 'CA'];
export const mercadoSchema = z.enum(MERCADOS);
// Plataformas cuyos mercados alimentan el módulo Secretos (Fase 3b). La
// columna `plataformas.plataforma` es texto libre: esta constante es la
// única fuente de esos dos literales.
export const PLATAFORMAS_AMAZON = ['Amazon Seller', 'Amazon Vendor'];
export const plataformaInputSchema = z.object({
    // Bug de plataformas duplicadas: la reconciliación en edición debe emparejar
    // por id de fila (no por nombre) — id presente = actualiza esa fila exacta,
    // ausente = crea una fila nueva. Ver reemplazarPlataformas() en el service.
    id: z.string().uuid().optional(),
    plataforma: z.string().min(1).max(100),
    estado_permisos: z.enum(['no_solicitados', 'solicitados', 'autorizados']).default('no_solicitados'),
    delegar_permisos_a: z.array(z.string()).default([]),
    fecha_primer_reporte: z.string().date().optional(),
    frecuencia_reporte: z.enum(['semanal', 'quincenal', 'mensual', 'otra']).optional(),
    responsable: z.string().max(120).optional(),
    // Fase 3a: reemplaza a `permisos_detalle` (eliminado). Opcional: ausente =
    // no tocar los mercados guardados; [] = quitarlos todos.
    mercados: z.array(mercadoSchema)
        .refine(v => new Set(v).size === v.length, { message: 'Mercados repetidos' })
        .optional(),
});
export const contactoInputSchema = z.object({
    nombre: z.string().min(1).max(200),
    correo: z.string().email().max(200).optional(),
    telefono: z.string().max(50).optional(),
    rol: z.string().max(100).optional(),
    es_principal: z.boolean().default(false),
    // Prompt BK-04 — solo relevante cuando es_principal=false (un primario
    // siempre se copia); no se valida aquí que no se mande junto a
    // es_principal=true, es simplemente ignorado por obtenerCorreosClientesActivos().
    copiar_en_comunicaciones: z.boolean().default(false),
});
// Prompt BK-04 — campos que se vuelven obligatorios en cualquier estatus
// DISTINTO de 'pendiente' (borrador). Compartido entre clienteSchema
// (creación) y cambiarEstatus() en el servicio (transición de estatus sobre
// un registro ya existente) para no duplicar la lista en dos lugares.
export const CAMPOS_REQUERIDOS_NO_PENDIENTE = ['razon_social', 'level', 'territorio', 'giro'];
// F.6 §6 — dentro de un mismo cliente no se puede repetir la misma plataforma
// (comparación case-insensitive). Se reutiliza en alta y edición.
function tienePlataformasDuplicadas(lista) {
    const vistas = new Set();
    for (const p of lista) {
        const key = p.plataforma.trim().toLowerCase();
        if (vistas.has(key))
            return true;
        vistas.add(key);
    }
    return false;
}
// ── clienteSchema (SRS-01 §2.4) ───────────────────────────────────────────────
export const clienteSchema = z
    .object({
    nombre_comercial: z.string().min(1).max(200),
    // Prompt BK-04 — solo nombre_comercial es obligatorio incondicional; el
    // resto se vuelve requerido vía superRefine cuando estatus !== 'pendiente'.
    razon_social: z.string().max(300).optional(),
    tipo_entrada: z.enum(['servicio', 'complemento', 'refacturacion', 'ads', 'otro']),
    cliente_principal_id: z.string().uuid().optional(),
    concepto: z.string().max(255).optional(),
    customer_type: z.enum(['empresa', 'persona_fisica']).default('empresa'),
    level: z.enum(['T1', 'T2', 'T3']).optional(),
    territorio: z.enum(['nacional', 'extranjero']).optional(),
    pais_operacion: z.string().max(100).optional(),
    origen_lead: z.string().max(100).optional(),
    billing_currency: z.enum(['MXN', 'USD', 'EUR']).default('MXN'),
    giro: z.string().max(100).optional(),
    idioma: z.enum(['espanol', 'ingles', 'portugues']).default('espanol'),
    // Prompt BK-04 — opcional en CUALQUIER estatus (reemplaza "Inicio de
    // Contrato"), nunca aparece en CAMPOS_REQUERIDOS_NO_PENDIENTE.
    fecha_inicio_servicio: z.string().date().optional(),
    // Prompt BK-04 — parte del payload de creación (antes se creaba siempre
    // como 'activo' y se cambiaba con un PATCH /:id/estatus aparte; ahora el
    // estatus real de creación determina qué campos son obligatorios, así que
    // debe conocerse ANTES del insert, no después).
    estatus: z.enum(['activo', 'pausado', 'pendiente', 'fuera_del_servicio']).default('activo'),
    prioridad: z.enum(['A', 'B', 'C']).optional(),
    comisiones_ads: z.boolean().default(false),
    fecha_inicio_comisiones_ads: z.string().date().optional(),
    terminacion_contrato: z.string().date().optional(),
    doc_plan_trabajo: z.boolean().default(false),
    doc_carta_intencion: z.boolean().default(false),
    doc_propuesta: z.boolean().default(false),
    doc_contrato: z.boolean().default(false),
    doc_sla: z.boolean().default(false),
    doc_nda: z.boolean().default(false),
    // Prompt BK-04 — multi-KAM, reemplaza kam_asignado. Array de usuario_id;
    // el servicio valida rol='kam' antes de insertar en cliente_kams.
    kam_ids: z.array(z.string().uuid()).default([]),
    involucrados: z.array(z.string()).default([]),
    brand_registry: z.boolean().default(false),
    brand_registry_url: urlOrEmpty.optional(),
    carpeta_assets: urlOrEmpty.optional(),
    anotaciones: z.string().max(500).optional(),
    // Entidades relacionadas creadas de forma inline al dar de alta el cliente
    plataformas: z.array(plataformaInputSchema).default([]),
    contactos: z.array(contactoInputSchema).default([]),
})
    .superRefine((data, ctx) => {
    // tipo_entrada !== 'servicio' → cliente_principal_id obligatorio
    if (data.tipo_entrada !== 'servicio' && !data.cliente_principal_id) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['cliente_principal_id'],
            message: 'cliente_principal_id es obligatorio cuando tipo_entrada no es "servicio"',
        });
    }
    // territorio === 'extranjero' → pais_operacion obligatorio
    if (data.territorio === 'extranjero' && !data.pais_operacion) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['pais_operacion'],
            message: 'pais_operacion es obligatorio cuando territorio es "extranjero"',
        });
    }
    if (tienePlataformasDuplicadas(data.plataformas)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['plataformas'],
            message: 'No se puede agregar la misma plataforma más de una vez',
        });
    }
    // Prompt BK-04 — borrador "Pendiente": único estatus donde el resto del
    // esquema puede estar incompleto. Cualquier otro estatus exige el
    // esquema completo vigente (fecha_inicio_servicio queda EXCLUIDA
    // siempre, ver comentario en su definición arriba).
    if (data.estatus !== 'pendiente') {
        for (const campo of CAMPOS_REQUERIDOS_NO_PENDIENTE) {
            if (!data[campo]) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [campo],
                    message: `${campo} es obligatorio cuando el estatus no es "pendiente"`,
                });
            }
        }
        if (data.kam_ids.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['kam_ids'],
                message: 'kam_ids es obligatorio (al menos un KAM) cuando el estatus no es "pendiente"',
            });
        }
    }
});
// ── actualizarClienteSchema ───────────────────────────────────────────────────
// Todos los campos del cliente son opcionales en actualizaciones parciales.
// 'estatus' se cambia exclusivamente por el endpoint PATCH /:id/estatus.
export const actualizarClienteSchema = z
    .object({
    nombre_comercial: z.string().min(1).max(200).optional(),
    razon_social: z.string().min(1).max(300).optional(),
    tipo_entrada: z.enum(['servicio', 'complemento', 'refacturacion', 'ads', 'otro']).optional(),
    cliente_principal_id: z.string().uuid().nullable().optional(),
    concepto: z.string().max(255).nullable().optional(),
    customer_type: z.enum(['empresa', 'persona_fisica']).optional(),
    level: z.enum(['T1', 'T2', 'T3']).optional(),
    territorio: z.enum(['nacional', 'extranjero']).optional(),
    pais_operacion: z.string().max(100).nullable().optional(),
    origen_lead: z.string().max(100).nullable().optional(),
    billing_currency: z.enum(['MXN', 'USD', 'EUR']).optional(),
    giro: z.string().min(1).max(100).optional(),
    idioma: z.enum(['espanol', 'ingles', 'portugues']).optional(),
    fecha_inicio_servicio: z.string().date().optional(),
    prioridad: z.enum(['A', 'B', 'C']).nullable().optional(),
    comisiones_ads: z.boolean().optional(),
    fecha_inicio_comisiones_ads: z.string().date().nullable().optional(),
    terminacion_contrato: z.string().date().nullable().optional(),
    doc_plan_trabajo: z.boolean().optional(),
    doc_carta_intencion: z.boolean().optional(),
    doc_propuesta: z.boolean().optional(),
    doc_contrato: z.boolean().optional(),
    doc_sla: z.boolean().optional(),
    doc_nda: z.boolean().optional(),
    // Prompt BK-04 — si se manda, reemplaza el set completo existente de
    // KAMs (mismo criterio que plataformas/contactos abajo).
    kam_ids: z.array(z.string().uuid()).optional(),
    involucrados: z.array(z.string()).optional(),
    brand_registry: z.boolean().optional(),
    brand_registry_url: urlOrEmpty.nullable().optional(),
    carpeta_assets: urlOrEmpty.nullable().optional(),
    anotaciones: z.string().max(500).nullable().optional(),
    // F.6 §2 — opcionales: si se mandan, reemplazan el set completo existente
    // de plataformas/contactos del cliente (ver actualizarCliente() en el
    // service). Si se omiten, no se tocan los que ya existían.
    plataformas: z.array(plataformaInputSchema).optional(),
    contactos: z.array(contactoInputSchema).optional(),
})
    .superRefine((data, ctx) => {
    if (data.tipo_entrada && data.tipo_entrada !== 'servicio' && data.cliente_principal_id === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['cliente_principal_id'],
            message: 'cliente_principal_id es obligatorio cuando tipo_entrada no es "servicio"',
        });
    }
    if (data.territorio === 'extranjero' && data.pais_operacion === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['pais_operacion'],
            message: 'pais_operacion es obligatorio cuando territorio es "extranjero"',
        });
    }
    if (data.plataformas && tienePlataformasDuplicadas(data.plataformas)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['plataformas'],
            message: 'No se puede agregar la misma plataforma más de una vez',
        });
    }
});
// ── cambiarEstatusSchema ──────────────────────────────────────────────────────
export const cambiarEstatusSchema = z.object({
    estatus: z.enum(['activo', 'pausado', 'pendiente', 'fuera_del_servicio']),
});
//# sourceMappingURL=clientes.js.map